import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shield, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

export const Privacy = () => {
  const { t } = useTranslation();

  const sections = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="relative min-h-screen pt-24 pb-16 px-4 bg-[#03050b] text-slate-200 selection:bg-cyan-500/30">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-cyan-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>
      
      <div className="relative max-w-5xl mx-auto z-10">
        <SEO
          title={`${t('navigation.privacy') || 'Privacy'} – Victor Lazari`}
          description={t('privacy.title') as string}
          canonical={"https://victorlazari.com/privacy"}
          image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
          type="website"
        />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-cyan-400">
            <Shield className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-[0.3em]">Legal Protocol</span>
          </div>
          <h1 className="mt-8 text-4xl md:text-6xl font-black text-white tracking-tight leading-none">
            {t('privacy.title')}
          </h1>
        </motion.div>

        <div className="space-y-8">
          {sections.map((section) => {
            const items = t(`privacy.section${section}.items`, { returnObjects: true, defaultValue: [] });

            return (
              <motion.section
                key={section}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                className="rounded-3xl border border-white/10 bg-white/[0.02] px-6 py-8 md:px-10 md:py-10 hover:bg-white/[0.04] transition-colors duration-500"
              >
                <h2 className="text-xl font-bold text-white mb-6 leading-tight flex items-center gap-3">
                  <span className="text-cyan-500/50 text-sm font-mono">0{section}</span>
                  {t(`privacy.section${section}.title`)}
                </h2>

                <p className="text-base text-slate-400 leading-relaxed mb-6 font-light">
                  {t(`privacy.section${section}.content`)}
                </p>

                {Array.isArray(items) && items.length > 0 && (
                  <ul className="space-y-3 text-sm text-slate-400">
                    {items.map((item: any, index: number) => (
                      <li key={index} className="flex items-start gap-3 leading-relaxed group">
                        <ChevronRight className="w-4 h-4 text-cyan-500/50 mt-1 flex-shrink-0 group-hover:text-cyan-400 transition-colors" />
                        <span className="group-hover:text-slate-300 transition-colors">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section === 9 && (
                  <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 px-6 py-6 text-sm text-slate-400 font-mono">
                    <p className="font-bold text-white mb-4 text-xs uppercase tracking-widest">{t('privacy.section9.contact.title')}</p>
                    <div className="space-y-2">
                      <p className="flex justify-between border-b border-white/5 pb-2">
                        <span>EMAIL</span>
                        <span className="text-cyan-400">{t('privacy.section9.contact.email')}</span>
                      </p>
                      <p className="flex justify-between border-b border-white/5 pb-2">
                        <span>PHONE</span>
                        <span className="text-white">{t('privacy.section9.contact.phone')}</span>
                      </p>
                      <p className="pt-2 text-right opacity-70">{t('privacy.section9.contact.address')}</p>
                    </div>
                  </div>
                )}
              </motion.section>
            );
          })}

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs text-slate-600 text-center mt-16 font-mono uppercase tracking-widest"
          >
            {t('privacy.lastUpdated')} // SECURE_LOG_TIMESTAMP
          </motion.p>
        </div>
      </div>
    </div>
  );
}; 